import { __decorate } from "tslib";
import React, { createElement } from 'react';
import moment, { isMoment } from 'moment';
import classNames from 'classnames';
import raf from 'raf';
import defaultTo from 'lodash/defaultTo';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import { observer } from 'mobx-react';
import { action, computed, isArrayLike, observable, runInAction } from 'mobx';
import { TimeStep } from 'choerodon-ui/dataset/interface';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import warning from 'choerodon-ui/lib/_util/warning';
import TriggerField from '../trigger-field/TriggerField';
import DaysView from './DaysView';
import DateTimesView from './DateTimesView';
import WeeksView from './WeeksView';
import TimesView from './TimesView';
import MonthsView from './MonthsView';
import YearsView from './YearsView';
import DecadeYearsView from './DecadeYearsView';
import autobind from '../_util/autobind';
import { ViewMode } from './enum';
import { stopEvent } from '../_util/EventManager';
import { $l } from '../locale-context';
import isSame from '../_util/isSame';
import measureTextWidth from '../_util/measureTextWidth';
import ObserverTextField from '../text-field/TextField';
export { TimeStep };
const viewComponents = {
    [ViewMode.decade]: DecadeYearsView,
    [ViewMode.year]: YearsView,
    [ViewMode.month]: MonthsView,
    [ViewMode.date]: DaysView,
    [ViewMode.dateTime]: DateTimesView,
    [ViewMode.week]: WeeksView,
    [ViewMode.time]: TimesView,
};
function createDefaultTime() {
    return moment('00:00:00', 'HH:mm:ss');
}
function getInRangeDefaultTime(defaultTime = createDefaultTime(), min, max, viewMode) {
    if (min && defaultTime.isBefore(min)) {
        defaultTime.year(min.year());
        defaultTime.month(min.month());
        defaultTime.date(min.date());
        if (defaultTime.isBefore(min) && viewMode !== ViewMode.time) {
            defaultTime.add(1, 'd');
        }
    }
    if (max && defaultTime.isAfter(max)) {
        defaultTime.year(max.year());
        defaultTime.month(max.month());
        defaultTime.date(max.date());
        if (defaultTime.isAfter(max) && viewMode !== ViewMode.time) {
            defaultTime.subtract(1, 'd');
        }
    }
    return defaultTime;
}
let DatePicker = class DatePicker extends TriggerField {
    constructor() {
        super(...arguments);
        this.handleDateMouseEnter = (currentDate) => {
            this.hoverValue = currentDate;
        };
        this.handleDateMouseLeave = () => {
            this.hoverValue = undefined;
        };
    }
    componentWillUnmount() {
        if (this.timeID) {
            clearTimeout(this.timeID);
            delete this.timeID;
        }
    }
    get value() {
        const { value } = this.observableProps;
        const { range } = this;
        if (isArrayLike(value)) {
            return value.map(item => {
                if (isArrayLike(item)) {
                    return item.map(this.checkMoment, this);
                }
                return this.checkMoment(item);
            });
        }
        if (isArrayLike(range)) {
            if (isPlainObject(value)) {
                const [start, end] = range;
                return {
                    [start]: this.checkMoment(value[start]),
                    [end]: this.checkMoment(value[end]),
                };
            }
        }
        return this.checkMoment(value);
    }
    set value(value) {
        runInAction(() => {
            this.observableProps.value = value;
        });
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('DatePicker', label ? 'value_missing' : 'value_missing_no_label', {
                label,
            }),
        };
    }
    get min() {
        return this.getLimit('min');
    }
    get max() {
        return this.getLimit('max');
    }
    savePopupInputEditor(node) {
        this.popupInputEditor = node;
        if (node && this.popup) {
            raf(() => {
                node.focus();
            });
        }
    }
    isEditable() {
        return super.isEditable() && !this.isEditableLike() && this.getViewMode() !== ViewMode.week;
    }
    isEditableLike() {
        return this.popup && this.observableProps.editorInPopup;
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'mode',
            'filter',
            'cellRenderer',
            'maxLength',
            'minLength',
            'timeZone',
            'editorInPopup',
            'defaultTime',
            'useInvalidDate',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        delete otherProps.maxLength;
        return otherProps;
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            editorInPopup: props.editorInPopup,
        };
    }
    defaultRenderer(props) {
        const renderedText = super.defaultRenderer(props);
        const mode = this.getDefaultViewMode();
        const { value } = props;
        if (value && [ViewMode.time, ViewMode.dateTime].includes(mode)) {
            const { timeZone = this.getContextConfig('formatter').timeZone } = this.props;
            if (timeZone) {
                const renderedTimeZone = typeof timeZone === 'function' ? timeZone(value) : value.format(timeZone);
                if (isString(renderedTimeZone)) {
                    return `${renderedText}${renderedTimeZone}`;
                }
                return [renderedText, renderedTimeZone];
            }
        }
        return renderedText;
    }
    getDefaultTime() {
        const { defaultTime = createDefaultTime() } = this.props;
        const viewMode = this.getDefaultViewMode();
        const { min, max } = this;
        if (isArrayLike(defaultTime)) {
            return [getInRangeDefaultTime(defaultTime[0], min, max, viewMode), getInRangeDefaultTime(defaultTime[1], min, max, viewMode)];
        }
        const inRangeDefaultTime = getInRangeDefaultTime(defaultTime, min, max, viewMode);
        return [inRangeDefaultTime, inRangeDefaultTime];
    }
    getDefaultViewMode() {
        const mode = this.getProp('mode', 'dateMode');
        if (mode === ViewMode.decade || mode === undefined) {
            return ViewMode.date;
        }
        return mode;
    }
    getPopupClassName(defaultClassName) {
        const viewMode = this.getViewMode();
        return classNames(super.getPopupClassName(defaultClassName), {
            [`${this.prefixCls}-popup-${String(viewMode).toLowerCase()}`]: viewMode,
        });
    }
    handlePopupRangeEditorBlur() {
        const { text } = this;
        if (text) {
            this.syncValueOnBlur(text);
        }
    }
    getPopupEditor() {
        const { editorInPopup } = this.observableProps;
        if (editorInPopup) {
            const { prefixCls, range, text, rangeTarget, multiple } = this;
            const popupHoverValue = this.getHoverValue(true);
            const className = classNames(`${prefixCls}-popup-editor`, {
                [`${prefixCls}-popup-hover-value`]: !isNil(popupHoverValue) && !range,
                [`${prefixCls}-popup-hover-value-start`]: !isNil(popupHoverValue) && range && rangeTarget === 0,
                [`${prefixCls}-popup-hover-value-end`]: !isNil(popupHoverValue) && range && rangeTarget === 1,
            });
            const [startPlaceholder, endPlaceHolder = startPlaceholder] = this.getPlaceholders();
            if (range) {
                const [startValue = '', endValue = ''] = this.processRangeValue(this.rangeValue);
                const startText = this.getText(startValue);
                const endText = this.getText(endValue);
                return (React.createElement("span", { key: "popup-editor", className: classNames(className, `${prefixCls}-range-text`) },
                    React.createElement("input", { className: `${prefixCls}-range-start`, onChange: this.handleChange, onFocus: this.handleRangeStart, onBlur: this.handlePopupRangeEditorBlur, value: rangeTarget === 0 ? defaultTo(defaultTo(popupHoverValue, text), startText) : startText, placeholder: startPlaceholder, ref: rangeTarget === 0 ? this.savePopupInputEditor : undefined, onKeyDown: this.handlePopupEditorKeyDown }),
                    React.createElement("span", { className: `${prefixCls}-range-split` }, "~"),
                    React.createElement("input", { className: `${prefixCls}-range-end`, onChange: this.handleChange, onFocus: this.handleRangeEnd, onBlur: this.handlePopupRangeEditorBlur, value: rangeTarget === 1 ? defaultTo(defaultTo(popupHoverValue, text), endText) : endText, placeholder: endPlaceHolder, ref: rangeTarget === 1 ? this.savePopupInputEditor : undefined, onKeyDown: this.handlePopupEditorKeyDown })));
            }
            const value = isNil(popupHoverValue) ? (multiple ? defaultTo(text, '') : this.getTextNode()) : popupHoverValue;
            return (React.createElement(ObserverTextField, { key: "popup-editor", value: value, onInput: this.handleChange, border: false, className: className, placeholder: startPlaceholder, onKeyDown: this.handlePopupEditorKeyDown, ref: this.savePopupInputEditor }));
        }
    }
    getHoverValue(isPopup) {
        const { editorInPopup } = this.props;
        if (((isPopup && editorInPopup) || (!isPopup && !editorInPopup))) {
            const { hoverValue } = this;
            if (hoverValue) {
                return hoverValue.format(this.getDateFormat());
            }
        }
    }
    // 处理 hover 值显示
    getEditorTextInfo(rangeTarget) {
        const { isFlat } = this.props;
        const hoverValue = this.getHoverValue(false);
        if (hoverValue !== undefined) {
            if (rangeTarget === undefined || (rangeTarget === 0 && this.rangeTarget === 0) || (rangeTarget === 1 && this.rangeTarget === 1)) {
                return {
                    text: hoverValue,
                    width: isFlat ? measureTextWidth(hoverValue) : 0,
                };
            }
        }
        return super.getEditorTextInfo(rangeTarget);
    }
    getRangeInputValue(startText, endText) {
        const hoverValue = this.getHoverValue(false);
        if (hoverValue === undefined) {
            return super.getRangeInputValue(startText, endText);
        }
        return hoverValue;
    }
    getInputClassString(className) {
        const { prefixCls } = this;
        const hoverValue = this.getHoverValue(false);
        return classNames(className, {
            [`${prefixCls}-hover-value`]: !isNil(hoverValue),
        });
    }
    getPopupContent() {
        const mode = this.getViewMode();
        const date = this.getCursorDate();
        return (React.createElement(React.Fragment, null,
            this.getPopupEditor(),
            createElement(viewComponents[mode], {
                ref: node => (this.view = node),
                date,
                mode: this.getDefaultViewMode(),
                disabledNow: !this.isValidNowDate(date),
                renderer: this.getCellRenderer(mode),
                onSelect: this.handleSelect,
                onSelectedDateChange: this.handleSelectedDateChange,
                onCursorDateChange: this.handleCursorDateChange,
                onViewModeChange: this.handelViewModeChange,
                isValidDate: this.isValidDate,
                format: this.getDateFormat(),
                step: this.getProp('step') || {},
                renderExtraFooter: this.getProp('renderExtraFooter'),
                extraFooterPlacement: this.getProp('extraFooterPlacement') || 'bottom',
                onDateMouseEnter: this.handleDateMouseEnter,
                onDateMouseLeave: this.handleDateMouseLeave,
                okButton: this.getContextConfig('dateTimePickerOkButton'),
            })));
    }
    getCellRenderer(mode) {
        const { cellRenderer = noop } = this.props;
        return cellRenderer(mode);
    }
    getTriggerIconFont() {
        return 'date_range';
    }
    getFieldType() {
        return viewComponents[this.getDefaultViewMode()].type;
    }
    getViewMode() {
        const { mode = this.getDefaultViewMode() } = this;
        return mode;
    }
    toMoment(item, field = this.field, noCheck = false) {
        if (isNil(item)) {
            return undefined;
        }
        if (isMoment(item)) {
            return item;
        }
        warning(noCheck, `DatePicker: The value of DatePicker is not moment.`);
        const format = this.getDateFormat(field);
        if (item instanceof Date) {
            item = moment(item).format(format);
        }
        const { range, rangeTarget } = this;
        const date = moment(item, format);
        if (date.isValid()) {
            const { unusedTokens } = date.parsingFlags();
            if (unusedTokens.includes('HH') && unusedTokens.includes('mm') && unusedTokens.includes('ss')) {
                const defaultTime = this.getDefaultTime()[range && rangeTarget !== undefined ? rangeTarget : 0];
                date.hour(defaultTime.hour());
                date.minute(defaultTime.minute());
                date.second(defaultTime.second());
            }
        }
        return date;
    }
    checkMoment(item) {
        return this.toMoment(item);
    }
    compare(oldValue, newValue) {
        return isSame(this.momentToTimestamp(oldValue), this.momentToTimestamp(newValue));
    }
    setText(text) {
        super.setText(text);
        if (text) {
            const date = this.toMoment(text);
            if (date && date.isValid()) {
                this.changeCursorDate(date);
            }
        }
    }
    afterSetValue() {
        super.afterSetValue();
        this.setText(undefined);
    }
    momentToTimestamp(value) {
        if (isMoment(value)) {
            return moment(value).valueOf();
        }
        return value;
    }
    getSelectedDate() {
        return this.getCursorDate();
    }
    getCursorDate() {
        const { range, multiple, rangeTarget, rangeValue } = this;
        let cursorDate = this.cursorDate ||
            (range && rangeTarget !== undefined && rangeValue && rangeValue[rangeTarget]) ||
            (!multiple && this.getValue());
        if (range && !multiple && rangeTarget !== undefined && !isNil(cursorDate) && !isMoment(cursorDate)) {
            cursorDate = typeof range === 'object' ? cursorDate[range[rangeTarget]] : cursorDate[rangeTarget];
        }
        if (isMoment(cursorDate) && cursorDate.isValid()) {
            return cursorDate.clone();
        }
        return this.getValidDate(this.getDefaultTime()[range && rangeTarget !== undefined ? rangeTarget : 0]);
    }
    getLimit(minOrMax) {
        let limit = this.getProp(minOrMax);
        if (isNil(limit)) {
            const configLimit = this.getContextConfig(minOrMax);
            if (configLimit) {
                limit = configLimit(this.getFieldType());
            }
        }
        if (!isNil(limit)) {
            const { record } = this;
            if (record && isString(limit)) {
                const field = record.dataSet.getField(limit);
                if (field) {
                    const value = record.get(limit);
                    if (value) {
                        const momentValue = this.toMoment(value, field, true);
                        if (momentValue) {
                            return this.getLimitWithType(momentValue.clone(), minOrMax);
                        }
                    }
                    return undefined;
                }
            }
            return this.getLimitWithType(moment(limit), minOrMax);
        }
    }
    getLimitWithType(limit, minOrMax) {
        if (minOrMax === 'min') {
            return limit.startOf('d');
        }
        return limit.endOf('d');
    }
    getPopupStyleFromAlign() {
        return undefined;
    }
    handleCursorDateChange(cursorDate, selectedDate, mode) {
        const nowDate = moment();
        if (this.getDefaultViewMode() === ViewMode.time && cursorDate.format('YYYY-MM-DD') !== nowDate.format('YYYY-MM-DD')) {
            // 更改为当天的 时间
            cursorDate = cursorDate.set({ 'year': nowDate.year(), 'month': nowDate.month(), 'date': nowDate.date() });
        }
        const { min, max } = this;
        if (!this.isUnderRange(cursorDate, mode)) {
            if (min && cursorDate.isSameOrBefore(min) && selectedDate.isSameOrBefore(cursorDate)) {
                // 往后翻页，跳转到 min
                cursorDate = min;
            }
            else if (max && cursorDate.isSameOrAfter(max) && selectedDate.isSameOrAfter(cursorDate)) {
                // 往前翻页，跳转到 max
                cursorDate = max;
            }
        }
        if (this.isUnderRange(cursorDate, mode) && ((mode && mode !== ViewMode.date) || this.isDateOutOfFilter(cursorDate, selectedDate, mode))) {
            this.changeCursorDate(cursorDate);
        }
    }
    handleSelectedDateChange(selectedDate, mode) {
        if (this.isUnderRange(selectedDate, mode) && this.isDateOutOfFilter(selectedDate, selectedDate, mode)) {
            this.setText(selectedDate.format(this.getDateFormat()));
        }
    }
    handelViewModeChange(mode) {
        runInAction(() => {
            this.hoverValue = undefined;
            this.mode = mode;
        });
    }
    handlePopupAnimateAppear() {
        // noop
    }
    handlePopupAnimateEnd(key, exists) {
        if (key === 'align') {
            if (exists) {
                const { popupInputEditor } = this;
                if (popupInputEditor) {
                    raf(() => {
                        popupInputEditor.focus();
                    });
                }
            }
            else {
                runInAction(() => {
                    this.cursorDate = undefined;
                    this.mode = undefined;
                });
            }
        }
    }
    handleSelect(date, expand) {
        if (this.multiple && this.isSelected(date)) {
            this.unChoose(date);
        }
        else {
            this.choose(date, expand);
        }
    }
    handleKeyDown(e) {
        if (!this.disabled && !this.readOnly) {
            const el = this.popup ? this.view || this : this;
            switch (e.keyCode) {
                case KeyCode.RIGHT:
                    el.handleKeyDownRight(e);
                    break;
                case KeyCode.LEFT:
                    el.handleKeyDownLeft(e);
                    break;
                case KeyCode.DOWN:
                    el.handleKeyDownDown(e);
                    break;
                case KeyCode.UP:
                    el.handleKeyDownUp(e);
                    break;
                case KeyCode.END:
                    el.handleKeyDownEnd(e);
                    break;
                case KeyCode.HOME:
                    el.handleKeyDownHome(e);
                    break;
                case KeyCode.PAGE_UP:
                    el.handleKeyDownPageUp(e);
                    break;
                case KeyCode.PAGE_DOWN:
                    el.handleKeyDownPageDown(e);
                    break;
                case KeyCode.ENTER:
                    el.handleKeyDownEnter(e);
                    break;
                case KeyCode.TAB:
                    this.handleKeyDownTab();
                    break;
                case KeyCode.ESC:
                    this.handleKeyDownEsc(e);
                    break;
                case KeyCode.SPACE:
                    this.handleKeyDownSpace(e);
                    break;
                case KeyCode.BACKSPACE:
                    this.handleKeyDownBackSpace(e);
                    break;
                case KeyCode.DELETE:
                    this.handleKeyDownDelete(e);
                    break;
                default:
            }
        }
        super.handleKeyDown(e);
    }
    handleKeyDownHome(e) {
        if (!this.multiple && !this.editable) {
            stopEvent(e);
            this.choose(this.getCursorDate().startOf('M'));
        }
    }
    handleKeyDownEnd(e) {
        if (!this.multiple && !this.editable) {
            stopEvent(e);
            this.choose(this.getCursorDate().endOf('M'));
        }
    }
    handleKeyDownLeft(e) {
        if (!this.multiple && !this.editable) {
            stopEvent(e);
            this.choose(this.getCursorDate().subtract(1, 'd'));
        }
    }
    handleKeyDownRight(e) {
        if (!this.multiple && !this.editable) {
            stopEvent(e);
            this.choose(this.getCursorDate().add(1, 'd'));
        }
    }
    handleKeyDownUp(e) {
        if (!this.multiple && !this.editable) {
            stopEvent(e);
            this.choose(this.getCursorDate().subtract(1, 'w'));
        }
    }
    handleKeyDownDown(e) {
        if (this.multiple) {
            this.expand();
        }
        else if (!this.editable) {
            stopEvent(e);
            this.choose(this.getCursorDate().add(1, 'w'));
        }
    }
    handleKeyDownPageUp(e) {
        if (!this.multiple && !this.editable) {
            stopEvent(e);
            this.choose(this.getCursorDate().subtract(1, e.altKey ? 'y' : 'M'));
        }
    }
    handleKeyDownPageDown(e) {
        if (!this.multiple && !this.editable) {
            stopEvent(e);
            this.choose(this.getCursorDate().add(1, e.altKey ? 'y' : 'M'));
        }
    }
    handleKeyDownEnter(_e) {
        if (!this.multiple && !this.editable) {
            this.choose(this.getCursorDate());
        }
    }
    handleKeyDownEsc(e) {
        if (this.popup) {
            e.preventDefault();
            this.collapse();
        }
    }
    handleKeyDownTab() {
        // this.collapse();
        if ((!this.range || this.rangeTarget === 1) && !isNil(this.hoverValue)) {
            this.hoverValue = undefined;
        }
    }
    handleKeyDownSpace(e) {
        if (!this.isEditable()) {
            e.preventDefault();
        }
        if (!this.popup) {
            this.expand();
        }
    }
    handleKeyDownBackSpace(e) {
        if (!this.isEditable() && !this.popup) {
            e.preventDefault();
        }
        if (this.cursorDate && this.afterKeyDownInputIsClear(e)) {
            runInAction(() => {
                this.cursorDate = undefined;
            });
        }
    }
    handleKeyDownDelete(e) {
        this.handleKeyDownBackSpace(e);
    }
    afterKeyDownInputIsClear(event) {
        const { keyCode, target: { value, selectionStart, selectionEnd } } = event;
        if (isNil(value)) {
            return true;
        }
        const length = String(value).length;
        const selectedLength = Math.abs(selectionEnd - selectionStart);
        if (length === selectedLength) {
            return true;
        }
        if (selectedLength !== 0) {
            return false;
        }
        if ((keyCode === KeyCode.BACKSPACE && (length === 1 && selectionStart === 1)) ||
            (keyCode === KeyCode.DELETE && (length === 1 && selectionStart === 0))) {
            return true;
        }
        return false;
    }
    handlePopupEditorKeyDown(e) {
        if (!this.disabled && !this.readOnly) {
            switch (e.keyCode) {
                case KeyCode.BACKSPACE:
                    this.handleKeyDownBackSpace(e);
                    break;
                case KeyCode.DELETE:
                    this.handleKeyDownDelete(e);
                    break;
                default:
            }
        }
    }
    handleEnterDown(e) {
        super.handleEnterDown(e);
        if (this.multiple && this.range) {
            this.setRangeTarget(0);
            this.beginRange();
            this.expand();
        }
    }
    prepareSetValue(...value) {
        let cursorDate;
        const { useInvalidDate } = this.props;
        super.prepareSetValue(...value.reduce((values, v) => {
            if (v === null) {
                values.push(null);
            }
            else {
                const m = this.checkMoment(v);
                if (m) {
                    if (useInvalidDate || m.isValid()) {
                        cursorDate = cursorDate || this.getCursorDate();
                        if (this.isDateOutOfFilter(m, cursorDate)) {
                            values.push(m);
                        }
                    }
                    else {
                        this.setText();
                    }
                }
                else {
                    values.push(m);
                }
            }
            return values;
        }, []));
    }
    syncValueOnBlur(value) {
        if (value) {
            this.prepareSetValue(value);
        }
        else if (!this.multiple) {
            this.prepareSetValue(this.emptyValue);
        }
    }
    getValueKey(v) {
        if (isArrayLike(v)) {
            return v.map(this.getValueKey, this).join(',');
        }
        if (isMoment(v)) {
            return v.format();
        }
        return v;
    }
    exchangeRangeValue(start, end) {
        this.rangeValueExchange = true;
        const { defaultTime } = this.props;
        if (defaultTime) {
            const [startDefaultTime, endDefaultTime] = this.getDefaultTime();
            const startHour = start.hour();
            const startMinute = start.minute();
            const startSecond = start.second();
            const startDefaultHour = startDefaultTime.hour();
            const startDefaultMinute = startDefaultTime.minute();
            const startDefaultSecond = startDefaultTime.second();
            const endHour = end.hour();
            const endMinute = end.minute();
            const endSecond = end.second();
            const endDefaultHour = endDefaultTime.hour();
            const endDefaultMinute = endDefaultTime.minute();
            const endDefaultSecond = endDefaultTime.second();
            if (startHour === startDefaultHour && startMinute === startDefaultMinute && startSecond === startDefaultSecond) {
                end.hour(startHour);
                end.minute(startMinute);
                end.second(startSecond);
            }
            if (endHour === endDefaultHour && endMinute === endDefaultMinute && endSecond === endDefaultSecond) {
                start.hour(endHour);
                start.minute(endMinute);
                start.second(endSecond);
            }
        }
        super.exchangeRangeValue(start, end);
    }
    changeCursorDate(cursorDate) {
        if (this.rangeValueExchange) {
            if (this.range && !isNil(this.rangeTarget) && this.rangeValue) {
                const [start, end] = [...this.rangeValue];
                cursorDate = this.rangeTarget === 0 ? start : end;
            }
            this.setText(undefined);
        }
        this.rangeValueExchange = false;
        this.cursorDate = cursorDate && this.getValidDate(cursorDate);
    }
    isSelected(date) {
        return this.getValues().some(value => date.isSame(value));
    }
    unChoose(date) {
        this.removeValue(date, -1);
    }
    /**
     *
     * @param date 返回的时间
     * @param expand 是否保持时间选择器的展开
     */
    choose(date, expand) {
        date = this.getValidDate(date);
        if (this.isDateOutOfFilter(date, date)) {
            this.prepareSetValue(date);
            this.changeCursorDate(date);
            const { range, rangeTarget } = this;
            if (range ? rangeTarget === 1 : !this.multiple) {
                if (!expand) {
                    this.collapse();
                }
            }
            if (range && rangeTarget === 0 && this.popup && !expand) {
                this.setRangeTarget(1);
            }
        }
    }
    setRangeTarget(target) {
        if (this.isFocused && (target !== undefined && target !== this.rangeTarget)) {
            this.expand();
        }
        if (!isNil(target)) {
            this.cursorDate = undefined;
        }
        else {
            if (isNil(this.cursorDate)) {
                this.cursorDate = this.getCursorDate();
            }
            this.timeID = window.setTimeout(action(() => {
                this.cursorDate = undefined;
            }), (this.props.triggerHiddenDelay || 50) + 20);
        }
        super.setRangeTarget(target);
    }
    getValidDate(date) {
        const { min, max } = this;
        if (min && date.isSameOrBefore(min)) {
            date = min;
        }
        else if (max && date.isSameOrAfter(max)) {
            date = max;
        }
        return date;
    }
    isLowerRange(m1, m2) {
        const moment1 = this.toMoment(m1);
        if (moment1) {
            return moment1.isBefore(this.toMoment(m2));
        }
        return false;
    }
    isUnderRange(date, mode) {
        const { min, max } = this;
        if (min || max) {
            let start = (min || date).clone();
            let end = (max || date).clone();
            switch (mode || this.getViewMode()) {
                case ViewMode.month:
                    start = start.startOf('M');
                    end = end.endOf('M');
                    break;
                case ViewMode.year:
                    start = start.startOf('y');
                    end = end.endOf('y');
                    break;
                case ViewMode.decade:
                    start = start
                        .startOf('y')
                        .subtract(start.year() % 10, 'y')
                        .startOf('d');
                    end = end
                        .endOf('y')
                        .add(9 - (end.year() % 10), 'y')
                        .endOf('d');
                    break;
                case ViewMode.dateTime:
                    start = start.startOf('d');
                    end = end.endOf('d');
                    break;
                default:
            }
            return date.isBetween(start, end, undefined, '[]');
        }
        return true;
    }
    isDateOutOfFilter(currentDate, selected, mode) {
        const { filter } = this.props;
        if (filter) {
            return filter(currentDate, selected, mode || this.getViewMode());
        }
        return true;
    }
    isValidDate(currentDate, selected, mode) {
        return this.isUnderRange(currentDate, mode) && this.isDateOutOfFilter(currentDate, selected, mode);
    }
    isValidNowDate(selected) {
        const { filter } = this.props;
        const isValid = this.isUnderRange(moment());
        if (isValid && filter) {
            return filter(moment(), selected);
        }
        return isValid;
    }
    getValidatorProp(key) {
        if (['maxLength', 'minLength'].includes(key)) {
            return;
        }
        switch (key) {
            case 'min':
            case 'max':
                return this[key];
            case 'format':
                return this.getDateFormat();
            default:
                return super.getValidatorProp(key);
        }
    }
    renderLengthInfo() {
        return undefined;
    }
};
DatePicker.displayName = 'DatePicker';
DatePicker.defaultProps = {
    ...TriggerField.defaultProps,
    suffixCls: 'calendar-picker',
    mode: ViewMode.date,
    useInvalidDate: true,
};
__decorate([
    computed
], DatePicker.prototype, "value", null);
__decorate([
    computed
], DatePicker.prototype, "min", null);
__decorate([
    computed
], DatePicker.prototype, "max", null);
__decorate([
    observable
], DatePicker.prototype, "cursorDate", void 0);
__decorate([
    observable
], DatePicker.prototype, "mode", void 0);
__decorate([
    observable
], DatePicker.prototype, "hoverValue", void 0);
__decorate([
    autobind
], DatePicker.prototype, "savePopupInputEditor", null);
__decorate([
    autobind
], DatePicker.prototype, "defaultRenderer", null);
__decorate([
    autobind,
    action
], DatePicker.prototype, "handlePopupRangeEditorBlur", null);
__decorate([
    action
], DatePicker.prototype, "handleDateMouseEnter", void 0);
__decorate([
    action
], DatePicker.prototype, "handleDateMouseLeave", void 0);
__decorate([
    autobind
], DatePicker.prototype, "compare", null);
__decorate([
    autobind
], DatePicker.prototype, "handleCursorDateChange", null);
__decorate([
    autobind
], DatePicker.prototype, "handleSelectedDateChange", null);
__decorate([
    autobind
], DatePicker.prototype, "handelViewModeChange", null);
__decorate([
    autobind
], DatePicker.prototype, "handlePopupAnimateEnd", null);
__decorate([
    autobind
], DatePicker.prototype, "handleSelect", null);
__decorate([
    autobind
], DatePicker.prototype, "handleKeyDown", null);
__decorate([
    action
], DatePicker.prototype, "handleKeyDownTab", null);
__decorate([
    autobind
], DatePicker.prototype, "handlePopupEditorKeyDown", null);
__decorate([
    action
], DatePicker.prototype, "handleEnterDown", null);
__decorate([
    action
], DatePicker.prototype, "prepareSetValue", null);
__decorate([
    autobind
], DatePicker.prototype, "getValueKey", null);
__decorate([
    action
], DatePicker.prototype, "changeCursorDate", null);
__decorate([
    action
], DatePicker.prototype, "setRangeTarget", null);
__decorate([
    autobind
], DatePicker.prototype, "isUnderRange", null);
__decorate([
    autobind
], DatePicker.prototype, "isValidDate", null);
__decorate([
    autobind
], DatePicker.prototype, "isValidNowDate", null);
DatePicker = __decorate([
    observer
], DatePicker);
export default DatePicker;
